import { Button, Kind } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import Badge from 'components/Badge'
import ConfirmModal from 'components/ConfirmModal'
import { StandaloneImagePicker } from 'components/ImagePicker/StandaloneImagePicker'
import EditNavbar from 'components/Navbar/EditNavbar'
import Section from 'components/Section'
import Spinner from 'components/Spinner'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import { ValidationTypes } from 'models/Validation'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { match as MatchType } from 'react-router-dom'
import { UrlPickerGeneric } from 'scenes/Cms/components/UrlPickerGeneric'
import BrameCampaignSelector from 'scenes/Cms/Content/AppNavigation/BottomNav/components/BrameCampaignSelector'
import { setFormValid } from 'slices/Cms/Content/AppNavigation'
import {
  closeDeleteModal,
  openDeleteModal,
  removeNavigationTab,
  saveDraft,
} from 'slices/Cms/Content/AppNavigation/BottomNav'
import {
  removeNavigationTabIcon,
  updateNavigationTab,
  updateNavigationTabIcon,
} from 'slices/Cms/Content/AppNavigation/BottomNav/Draft'
import {
  selectCanDeleteTabDraft,
  selectIsRestrictedTabDraft,
  selectNavigationTabByIdDraft,
  selectNavigationTabsDraft,
} from 'slices/Cms/Content/AppNavigation/BottomNav/Draft/selectors'
import {
  selectDeleteModal,
  selectNavigationTabById,
} from 'slices/Cms/Content/AppNavigation/BottomNav/selectors'
import { StateWithAppNavigation } from 'slices/Cms/Content/AppNavigation/types'
import { history } from 'store'
import { useAppNavigationInit } from '../hooks'
import { useFetchNavigationTabs, useInitDraft, useSyncApiState } from './hooks'

const t = buildTranslate('cms.content.app_navigation.bottom_nav.tab')
const tBlocks = buildTranslate('cms.components.blocks')

interface EditParams {
  id: string
}

interface EditProps {
  match?: MatchType<EditParams>
}

const Edit: React.FC<EditProps> = ({ match }) => {
  // Initialize app navigation data (loads landing page theme to get theme ID)
  useAppNavigationInit()

  // Load back-end API data (ideally just on mount)
  // * this is important if the user navigates via url/browser
  useFetchNavigationTabs()

  // Sync the local state with the API state
  // * this is important if the user navigates via url/browser
  useSyncApiState()

  // Initialize draft state with data from main BottomNav slice
  useInitDraft()

  const dispatch = useDispatch()
  const [css] = useStyletron()

  const tabId = match?.params?.id

  // CHANGE 4: Use draft selector instead of main selector
  const tab = useSelector((state: StateWithAppNavigation) =>
    tabId ? selectNavigationTabByIdDraft(state, tabId) : undefined
  )
  const rootTab = useSelector((state: StateWithAppNavigation) =>
    tabId ? selectNavigationTabById(state, tabId) : undefined
  )

  // CHANGE 5: Get draft tabs for save functionality
  const draftTabs = useSelector(selectNavigationTabsDraft)

  // Get delete modal state from main BottomNav slice
  const deleteModal = useSelector(selectDeleteModal)

  // Check if this tab is restricted (first or last tab)
  const canEditTab = useSelector((state: StateWithAppNavigation) =>
    tabId ? selectIsRestrictedTabDraft(state, tabId) : false
  )

  // Check if this tab can be deleted
  const canDeleteTab = useSelector((state: StateWithAppNavigation) =>
    tabId ? selectCanDeleteTabDraft(state, tabId) : false
  )

  const brameIntegrationEnabled = useFlag(
    'gamification-bottom-nav-enabled',
    false
  )

  // Redirect to bottom_nav if tab doesn't exist (e.g., on page refresh with dynamic ID)
  useEffect(() => {
    if (tabId && !rootTab) {
      history.push('/brand_content/app_navigation/bottom_nav')
    }
  }, [tabId, rootTab])

  useEffect(() => {
    // Skip validation for game tabs if Brame integration is disabled
    if (!brameIntegrationEnabled) {
      dispatch(setFormValid(true))
      return
    }

    const allTabsValid = Object.values(draftTabs).every(draftTab => {
      const isGameTab = draftTab.link?.inapp === 'thanx://game'
      return !isGameTab || !!draftTab.settings?.brame_campaign_id
    })
    dispatch(setFormValid(allTabsValid))
  }, [draftTabs, dispatch, brameIntegrationEnabled])

  useEffect(() => {
    return () => {
      dispatch(setFormValid(true))
    }
  }, [dispatch])

  if (!tab) {
    return <Spinner />
  }

  const handleDelete = () => {
    if (deleteModal.tabId) {
      dispatch(removeNavigationTab(deleteModal.tabId))
      dispatch(closeDeleteModal())
      history.push('/brand_content/app_navigation/bottom_nav')
    }
  }

  const handleOpenDeleteModal = () => {
    if (tabId && canDeleteTab) {
      dispatch(openDeleteModal(tabId))
    }
  }

  const handleCloseDeleteModal = () => {
    dispatch(closeDeleteModal())
  }

  // CHANGE 6: Update save handler to use saveDraft action
  const handleSave = () => {
    // Save draft data to main state
    dispatch(saveDraft(draftTabs))
    history.push('/brand_content/app_navigation/bottom_nav')
  }

  const handleDefaultIconChange = (fileUploadId: number) => {
    if (fileUploadId) {
      const iconUpdateData = {
        id: fileUploadId,
        url: '', // URL will be updated by the onChange callback
        file_upload_id: fileUploadId,
      }
      dispatch(
        updateNavigationTabIcon({
          tabId: tab.id,
          iconType: 'default_icon',
          iconData: iconUpdateData,
        })
      )
    } else {
      dispatch(
        removeNavigationTabIcon({
          tabId: tab.id,
          iconType: 'default_icon',
        })
      )
    }
  }

  const handleSelectedIconChange = (fileUploadId: number) => {
    if (fileUploadId) {
      const iconUpdateData = {
        id: fileUploadId,
        url: '', // URL will be updated by the onChange callback
        file_upload_id: fileUploadId,
      }
      dispatch(
        updateNavigationTabIcon({
          tabId: tab.id,
          iconType: 'selected_icon',
          iconData: iconUpdateData,
        })
      )
    } else {
      dispatch(
        removeNavigationTabIcon({
          tabId: tab.id,
          iconType: 'selected_icon',
        })
      )
    }
  }

  const handleDefaultIconValueChange = (newValue: {
    id: number | null
    record_id?: number | null
    url?: string
    uploading: boolean
    pendingImage?: string
  }) => {
    if (newValue.id && (newValue.url || newValue.pendingImage)) {
      dispatch(
        updateNavigationTabIcon({
          tabId: tab.id,
          iconType: 'default_icon',
          iconData: {
            id: newValue.id,
            url: newValue.url || '',
            file_upload_id: newValue.record_id || newValue.id,
            pendingImage: newValue.pendingImage,
          },
        })
      )
    }
  }

  const handleSelectedIconValueChange = (newValue: {
    id: number | null
    record_id?: number | null
    url?: string
    uploading: boolean
    pendingImage?: string
  }) => {
    if (newValue.id && (newValue.url || newValue.pendingImage)) {
      dispatch(
        updateNavigationTabIcon({
          tabId: tab.id,
          iconType: 'selected_icon',
          iconData: {
            id: newValue.id,
            url: newValue.url || '',
            file_upload_id: newValue.record_id || newValue.id,
            pendingImage: newValue.pendingImage,
          },
        })
      )
    }
  }

  const handleUrlChange = (urlData: any) => {
    // Don't allow changes to restricted tabs (first or last)
    if (!canEditTab) {
      return
    }

    dispatch(
      updateNavigationTab({
        id: tab.id,
        data: {
          link: urlData,
        },
      })
    )
  }

  const handleNameChange = (name: string) => {
    dispatch(
      updateNavigationTab({
        id: tab.id,
        data: { name },
      })
    )
  }

  const handleBrameCampaignChange = (campaignId: number | undefined) => {
    dispatch(
      updateNavigationTab({
        id: tab.id,
        data: {
          settings: {
            ...tab.settings,
            brame_campaign_id: campaignId,
          },
        },
      })
    )
  }

  const isGameTab = tab.link?.inapp === 'thanx://game'

  return (
    <div
      className="h-100 w-100"
      style={{
        position: 'fixed',
        top: '0px',
        paddingTop: '80px',
        left: '0px',
        display: 'flex',
      }}
    >
      <EditNavbar title={`Configure Tab - ${tab.name}`}>
        <Button
          kind={Kind.MINIMAL}
          className={css({ color: 'white' })}
          onClick={() =>
            history.push('/brand_content/app_navigation/bottom_nav')
          }
        >
          Cancel
        </Button>
        <Button
          kind={Kind.MINIMAL}
          className={css({ color: 'white' })}
          onClick={handleOpenDeleteModal}
          disabled={!canDeleteTab}
        >
          Remove tab
        </Button>
        <Button kind={Kind.SECONDARY_REVERSE} onClick={handleSave}>
          Save & return to CMS
        </Button>
      </EditNavbar>

      <div className="d-flex flex-1 white-bkg flex-column">
        <div className="d-flex flex-column h-100 grey-05-bkg overflow-scroll">
          <div className="px-0 container">
            <Text.Header3>Content</Text.Header3>
            <div className="flex-row mt-m mb-xl">
              <Badge
                className="grey-70 mr-xs grey-20-bkg d-inline"
                value="APP ONLY"
              />
              <Badge
                className="grey-70 mr-xs grey-20-bkg d-inline"
                value="72 X 72 PX"
              />
              <Badge
                className="grey-70 mr-xs grey-20-bkg d-inline"
                value="PNG"
              />
              <Badge
                className="grey-70 mr-xs grey-20-bkg d-inline"
                value="GIF"
              />
            </div>
            <Section className="flex-1 mb-l mr-xs p-l">
              {/* Default Icon Section */}
              <div className="mb-l">
                <StandaloneImagePicker
                  className="mb-l"
                  value={{
                    id: tab.default_icon?.id || null,
                    url: tab.default_icon?.url || '',
                    uploading: false,
                    pendingImage: tab.default_icon?.pendingImage,
                  }}
                  label={t('icon_default')}
                  onChange={handleDefaultIconValueChange}
                  onFinalized={handleDefaultIconChange}
                  allowedExtensions={['png', 'gif']}
                  maxSizeMB={4}
                  minWidth={72}
                  minHeight={72}
                  record_type={ValidationTypes.NavigationTabDefaultIcon}
                />
              </div>

              {/* Selected Icon Section */}
              <div className="mb-l">
                <StandaloneImagePicker
                  className="mb-l"
                  value={{
                    id: tab.selected_icon?.id || null,
                    url: tab.selected_icon?.url || '',
                    uploading: false,
                    pendingImage: tab.selected_icon?.pendingImage,
                  }}
                  label={t('icon_selected')}
                  onChange={handleSelectedIconValueChange}
                  onFinalized={handleSelectedIconChange}
                  allowedExtensions={['png', 'gif']}
                  maxSizeMB={4}
                  minWidth={72}
                  minHeight={72}
                  record_type={ValidationTypes.NavigationTabSelectedIcon}
                />
              </div>

              {/* Label Section */}
              <div className="mb-l">
                <Text.BodyText4 className="font-size-14 font-weight-bold">
                  {t('label')}
                </Text.BodyText4>
                <input
                  type="text"
                  value={tab.name}
                  onChange={e => handleNameChange(e.target.value)}
                  className="form-control"
                  placeholder="Enter tab label"
                />
              </div>

              {/* URL Picker Section */}
              <div className="mb-l">
                <Text.BodyText4 className="font-size-14 font-weight-bold">
                  {t('link_destination')}
                </Text.BodyText4>
                <UrlPickerGeneric
                  id={`tab-${tab.id}`}
                  type="image"
                  url={tab.link}
                  required={false}
                  onUrlChange={handleUrlChange}
                  hasQueryParam
                  disabled={!canEditTab}
                />
                {/*
                  Note: Menu Category selector is temporarily disabled since
                    some menu categories are disabled for some merchant locations.
                    In the future we may allow menu-category selection, but also
                    allow automatic hiding of tabs pointing to menu categories
                    not available at the current user location
                */}
                {/* {tab.link.type === UrlType.Inapp &&
                  tab.link.inapp?.startsWith('thanx://ordering-menu') && (
                    <MenuCategorySelector
                      value={tab.link.queryParams || ''}
                      onChange={handleMenuCategoryChange}
                      menuOptions={menuOptions}
                      isLoading={isLoadingMenuCategories}
                      disabled={!canEditTab}
                    />
                  )} */}
              </div>

              {isGameTab && brameIntegrationEnabled && (
                <BrameCampaignSelector
                  value={tab.settings?.brame_campaign_id}
                  onChange={handleBrameCampaignChange}
                  disabled={!canEditTab}
                />
              )}
            </Section>
          </div>
        </div>
      </div>

      <ConfirmModal
        title={tBlocks('delete_modal.title', { type: 'navigation tab' })}
        description={tBlocks('delete_modal.description', {
          type: 'navigation tab',
        })}
        confirmText={tBlocks('delete_modal.confirm', {
          type: 'navigation tab',
        })}
        isOpen={deleteModal.isOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDelete}
      />
    </div>
  )
}

export default Edit
